// src/components/common/EmployeeProfileView.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
    User, Mail, Phone, Calendar, Building, CreditCard,
    Download, UserCircle, X, Camera, ThumbsUp, MessageSquare, Award, Gift, ArrowRight
} from 'lucide-react';
import EmployeeDocuments from '../employee/EmployeeDocumentsEnhanced';
import TimeTab from './TimeTab';
import ActivityLogsTab from './ActivityLogsTab';
import AwardCompOffModal from './AwardCompOffModal';
import VibeFeed from '../common/vibe/VibeFeed';
import holidayPlanService from '../../services/holidayPlan.service';
import leavePlanService from '../../services/leavePlan.service';
import companySettingsService from '../../services/companySettings.service';
import functionalStructureService from '../../services/functionalStructure.service';
import shiftService from '../../services/shift.service';import { getProfileAccessPermissions, shouldHideContent, canViewTab } from '../../utils/profileAccessControl';
import { useShift } from '../../contexts/ShiftContext';
import { useAuth } from '../../hooks/useAuth';
import { useEmployees } from '../../contexts/EmployeeContext';
import { sessionCache } from '../../utils/sessionCache';
import BankDropdown from './BankDropdown';
import { getExitStatusBadge } from '../../utils/exitStatusHelper';
import { toISTDateString, getISTComponents } from '../../utils/dateHelpers';

// Helper function to format dates for display in IST
// The backend sends dates as ISO strings like "1999-01-10T18:30:00.000Z" which represents
// midnight IST (1999-01-11 00:00:00 IST) converted to UTC
const formatDateForDisplay = (dateStr, options = {}) => {
    if (!dateStr) return 'Not specified';

    // Get the IST date string first (YYYY-MM-DD)
    const istDateStr = toISTDateString(dateStr);
    if (!istDateStr || istDateStr === 'Invalid Date') return 'Not specified';

    // Parse the IST date string and create a date at noon IST for formatting
    const [year, month, day] = istDateStr.split('-').map(Number);
    // Use Date.UTC at 06:30 (noon IST) to ensure consistent formatting
    const safeDate = new Date(Date.UTC(year, month - 1, day, 6, 30, 0));

    // Format with IST timezone to ensure correct display
    return safeDate.toLocaleDateString('en-US', {
        ...options,
        timeZone: 'Asia/Kolkata'
    });
};

// Helper to get year from date in IST
const getYearFromDate = (dateStr) => {
    if (!dateStr) return '';
    const { year } = getISTComponents(dateStr);
    return year ? year.toString() : '';
};

const EmployeeProfileView = ({
    profile,
    employees = [],
    isEditable = false,
    editMode = false,
    editedProfile = {},
    onEditChange,
    onPhotoUpload,
    isOwnProfile = true,  // New prop to determine if viewing own profile
    currentUser = null  // Current logged-in user for access control
}) => {
    const { user } = useAuth();
    // Try to get fetchEmployees from EmployeeContext, but don't fail if not available
    let fetchEmployees = null;
    try {
        const employeeContext = useEmployees();
        fetchEmployees = employeeContext?.fetchEmployees;
    } catch (error) {
        // EmployeeContext not available, which is fine
        console.log('EmployeeContext not available in this context');
    }
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    // Initialize activeTab from URL parameter if present
    const [activeTab, setActiveTab] = useState(() => {
        const tabParam = searchParams.get('tab');
        return tabParam || 'about';
    });

    const { getShiftOptions } = useShift();
    const [holidayPlans, setHolidayPlans] = useState([]);
    const [leavePlans, setLeavePlans] = useState([]);
    const [shifts, setShifts] = useState([]);
    const [legalEntities, setLegalEntities] = useState([]);
    const [currentTheme, setCurrentTheme] = useState(() =>
        document.documentElement.classList.contains('dark') ? 'dark' : 'light'
    );

    // Function to get correct Vibe route based on current portal context
    const getVibeRoute = () => {
        // Check current path to determine portal context
        const currentPath = location.pathname;

        if (currentPath.startsWith('/admin/')) {
            return '/admin/vibe';
        } else if (currentPath.startsWith('/manager/')) {
            return '/manager/vibe';
        } else if (currentPath.startsWith('/employee/')) {
            return '/employee/vibe';
        } else {
            // Fallback to role-based logic
            if (!user?.role) return '/employee/vibe';
            switch (user.role) {
                case 'Super Admin':
                case 'HR Admin':
                    return '/admin/vibe';
                case 'Manager':
                    return '/manager/vibe';
                case 'Employee':
                default:
                    return '/employee/vibe';
            }
        }
    };
    const [departments, setDepartments] = useState([]);
    const [aboutSubTab, setAboutSubTab] = useState('summary');
    const [showManagerDropdown, setShowManagerDropdown] = useState(false);
    const [managerSearch, setManagerSearch] = useState('');
    const [showIDCard, setShowIDCard] = useState(false);
    const [showAwardCompOff, setShowAwardCompOff] = useState(false);

    // About section edit states
    const [editingAbout, setEditingAbout] = useState(false);
    const [editingJobLove, setEditingJobLove] = useState(false);
    const [editingHobbies, setEditingHobbies] = useState(false);
    const [aboutText, setAboutText] = useState('');
    const [jobLoveText, setJobLoveText] = useState('');
    const [hobbiesText, setHobbiesText] = useState('');
    const [savingAbout, setSavingAbout] = useState(false);

    const viewer = currentUser || user || null;

    const permissions = useMemo(() => {
        if (!profile || !viewer) {
            return {
                isOwnProfile,
                canViewAllTabs: true,
                canViewLimitedPanels: !isOwnProfile,
                canEditProfile: isEditable,
                canEditJob: isEditable,
                canEditTime: isEditable,
                canEditDocuments: isEditable,
                canAwardCompOff: false,
                showEditButton: isEditable,
                showLimitedJobSection: !isOwnProfile,
                role: isOwnProfile ? 'self' : 'employee'
            };
        }

        const computed = getProfileAccessPermissions(viewer, profile, employees);
        return {
            showLimitedJobSection: false,
            ...computed,
            showLimitedJobSection: computed.showLimitedJobSection ?? false
        };
    }, [profile, viewer, employees, isOwnProfile, isEditable]);

    // Get reporting manager details
    const reportingManager = employees.find(emp => emp.id === (profile?.managerId || profile?.reporting_manager_id));

    // Get reporting team (employees who report to this employee)
    const reportingTeam = employees.filter(emp => emp.managerId === profile?.id);

    // Determine current portal context
    const getCurrentPortal = () => {
        const path = location.pathname;
        if (path.startsWith('/admin')) return 'admin';
        if (path.startsWith('/manager')) return 'manager';
        if (path.startsWith('/employee')) return 'employee';
        return 'employee'; // default
    };

    // Handle navigation to employee profile
    const handleNavigateToProfile = (employeeId) => {
        const portal = getCurrentPortal();
        if (portal === 'admin') {
            navigate(`/admin/employees/${employeeId}`);
        } else if (portal === 'manager') {
            navigate(`/manager/view-profile/${employeeId}`);
        } else {
            navigate(`/employee/view-profile/${employeeId}`);
        }
    };

    // Check if user is admin or manager (for showing Logs tab)
    const isAdminOrManager = viewer?.is_admin ||
                             viewer?.isAdmin ||
                             viewer?.role === 'admin' ||
                             viewer?.role_name === 'Admin' ||
                             viewer?.role_name === 'HR Admin' ||
                             viewer?.role_name === 'Super Admin' ||
                             viewer?.access_level === 'admin' ||
                             viewer?.is_manager ||
                             viewer?.isManager ||
                             viewer?.role === 'manager' ||
                             viewer?.access_level === 'manager' ||
                             (viewer?.role_name && viewer.role_name.toLowerCase().includes('admin')) ||
                             (viewer?.role && typeof viewer.role === 'string' && viewer.role.toLowerCase().includes('admin'));

    // Filter tabs based on permissions
    const allTabs = [
        { id: 'about', label: 'About' },
        { id: 'profile', label: 'Profile' },
        { id: 'job', label: 'Job' },
        { id: 'time', label: 'Time' },
        { id: 'documents', label: 'Documents' },
        // Logs tab only visible to admins/managers when viewing other employees' profiles
        ...(isAdminOrManager && !isOwnProfile ? [{ id: 'logs', label: 'Logs' }] : [])
    ];

    const tabs = allTabs.filter(tab => canViewTab(tab.id, permissions));

    // Fetch holiday plans, leave plans, shifts, and legal entities for dropdowns
    // Debug: Log profile data when it changes
    useEffect(() => {
        if (profile) {
            console.log("🔍 Employee profile data:", {
                leave_plan: profile.leave_plan,
                leavePlan: profile.leavePlan,
                holiday_plan_id: profile.holiday_plan_id,
                holiday_list: profile.holiday_list,
                shift: profile.shift,
                shift_id: profile.shift_id
            });
        }
    }, [profile]);

    // Theme detection
    useEffect(() => {
        const handleThemeChange = () => {
            setCurrentTheme(document.documentElement.classList.contains('dark') ? 'dark' : 'light');
        };

        // Initial theme detection
        handleThemeChange();

        // Listen for theme changes (in case theme is toggled via a button or system preference)
        const observer = new MutationObserver(handleThemeChange);
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class']
        });

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [holidayData, leaveData, shiftData, functionalData] = await Promise.all([
                    holidayPlanService.getHolidayPlans(),
                    leavePlanService.getLeavePlans(),
                    shiftService.getAllShifts(),
                    functionalStructureService.getActive()
                ]);
                setHolidayPlans(Array.isArray(holidayData) ? holidayData : (holidayData?.plans || holidayData?.data || holidayData?.holidayPlans || []));
                console.log("🔍 Fetched holiday plans:", holidayPlans);
                console.log("🔍 Fetched leave plans:", leavePlans);                setLeavePlans(Array.isArray(leaveData) ? leaveData : (leaveData?.plans || leaveData?.data || leaveData?.leavePlans || []));
                setShifts(Array.isArray(shiftData) ? shiftData : (shiftData?.shifts || shiftData?.data || []));

                // Extract legal entities from functional structure
                if (functionalData && functionalData.structure_data) {
                    const entities = functionalData.structure_data.legalEntities || [];
                    const validEntities = entities.filter(e => !e.isNew && e.name && e.name !== 'Add Legal Entity');
                    setLegalEntities(validEntities);
                }
            } catch (error) {
                console.error('Error fetching dropdown data:', error);
            }
        };
        fetchData();
    }, []);

    // Update departments when legal entity changes
    // Debug: Log profile data when it changes
    useEffect(() => {
        if (profile) {
            console.log("🔍 Employee profile data:", {
                leave_plan: profile.leave_plan,
                leavePlan: profile.leavePlan,
                holiday_plan_id: profile.holiday_plan_id,
                holiday_list: profile.holiday_list,
                shift: profile.shift,
                shift_id: profile.shift_id
            });
        }
    }, [profile]);
    useEffect(() => {
        if (editedProfile.legal_entity && legalEntities.length > 0) {
            // If legal entity is selected, show only its departments
            const selectedEntity = legalEntities.find(e => e.name === editedProfile.legal_entity);
            if (selectedEntity) {
                setDepartments(selectedEntity.departments || []);
            } else {
                setDepartments([]);
            }
        } else if (legalEntities.length > 0) {
            // If no legal entity selected, show ALL departments from ALL legal entities
            const allDepartments = legalEntities.flatMap(entity => entity.departments || []);
            // Remove duplicates by department name
            const uniqueDepartments = Array.from(
                new Map(allDepartments.map(dept => [dept.name, dept])).values()
            );
            setDepartments(uniqueDepartments);
        } else if (profile?.legal_entity && legalEntities.length > 0) {
            // Set departments based on profile's legal entity for view mode
            const selectedEntity = legalEntities.find(e => e.name === profile.legal_entity);
            if (selectedEntity) {
                setDepartments(selectedEntity.departments || []);
            }
        }
    }, [editedProfile.legal_entity, profile?.legal_entity, legalEntities]);

    // Initialize About section fields from profile
    useEffect(() => {
        if (profile) {
            setAboutText(profile.about || '');
            setJobLoveText(profile.what_i_love_about_job || '');
            setHobbiesText(profile.interests_and_hobbies || '');
        }
    }, [profile]);

    // Handler to save About section fields
    const handleSaveAboutField = async (field, value) => {
        setSavingAbout(true);
        try {
            const token = localStorage.getItem('authToken');
            console.log('🔐 Saving about field:', { field, value, profileId: profile.id, token: token ? 'exists' : 'missing' });

            const response = await fetch(`${process.env.REACT_APP_API_URL}/employees/${profile.id}/about`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ [field]: value })
            });

            console.log('📡 Response status:', response.status);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                console.error('❌ Save failed:', { status: response.status, error: errorData });
                throw new Error(errorData.error || errorData.message || 'Failed to save');
            }

            const data = await response.json();
            console.log('✅ Save successful:', data);

            // Clear the employee cache to ensure fresh data is loaded on refresh
            sessionCache.clear('employees');
            console.log('🗑️ Cleared employee cache');

            // Refresh employee data from backend if fetchEmployees is available
            if (fetchEmployees) {
                await fetchEmployees(true);
                console.log('🔄 Refreshed employee data from backend');
            }

            // Update profile data if onEditChange is available
            if (onEditChange) {
                onEditChange({ target: { name: field, value } });
            }

            alert('Saved successfully!');
        } catch (error) {
            console.error('Error saving about field:', error);
            alert(`Failed to save: ${error.message}`);
        } finally {
            setSavingAbout(false);
        }
    };

    // Close manager dropdown when clicking outside
    // Debug: Log profile data when it changes
    useEffect(() => {
        if (profile) {
            console.log("🔍 Employee profile data:", {
                leave_plan: profile.leave_plan,
                leavePlan: profile.leavePlan,
                holiday_plan_id: profile.holiday_plan_id,
                holiday_list: profile.holiday_list,
                shift: profile.shift,
                shift_id: profile.shift_id
            });
        }
    }, [profile]);
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (showManagerDropdown && !e.target.closest('.relative')) {
                setShowManagerDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showManagerDropdown]);

    return (
        <div className="space-y-6">
            {/* Profile Header */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div className="flex items-start gap-6">
                    <div className="relative">
                        <div className="w-32 h-32 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                            {profile?.profile_picture_url || profile?.photo ? (
                                <img src={profile.profile_picture_url || profile.photo} alt="Profile" className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                    <User className="w-16 h-16 text-gray-400" />
                                </div>
                            )}
                        </div>
                        {isEditable && (
                            <label className="absolute bottom-0 right-0 bg-blue-600 rounded-full p-2 cursor-pointer hover:bg-blue-700">
                                <Camera className="w-4 h-4 text-white" />
                                <input type="file" className="hidden" accept="image/*" onChange={onPhotoUpload} />
                            </label>
                        )}
                    </div>

                    <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                {profile?.first_name} {profile?.last_name}
                            </h2>
                            {(() => {
                                const exitStatusBadge = getExitStatusBadge(profile, 'md');
                                return (
                                    <span
                                        className={exitStatusBadge.className}
                                        title={exitStatusBadge.description}
                                    >
                                        {exitStatusBadge.label}
                                    </span>
                                );
                            })()}
                        </div>
                        <p className="text-gray-600 dark:text-gray-400">{profile?.designation}</p>
                        <p className="text-gray-500 dark:text-gray-500">{profile?.employee_code}</p>

                        <div className="grid grid-cols-2 gap-4 mt-4">
                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                <Mail className="w-4 h-4" />
                                {profile?.email}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                <Phone className="w-4 h-4" />
                                {profile?.phone || 'Not provided'}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                <Building className="w-4 h-4" />
                                {profile?.department}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                <Calendar className="w-4 h-4" />
                                Joined {formatDateForDisplay(profile?.joiningDate || profile?.joining_date)}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Action Buttons - Only show Award Comp-Off when viewing other's profile (not own profile) */}
                {!isOwnProfile && permissions.canAwardCompOff && (
                    <div className="flex gap-3 mt-4">
                        {/* Award Comp-Off button - only for managers viewing direct reports or admins */}
                        <button
                            onClick={() => setShowAwardCompOff(true)}
                            className="flex items-center gap-2 px-4 py-2 border border-green-300 dark:border-green-600 text-green-700 dark:text-green-300 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                        >
                            <Gift className="w-4 h-4" />
                            Award Comp Off
                        </button>
                    </div>
                )}
            </div>

            {/* Info Panel - Legal Entity, Department, Reporting Manager, ID Card */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div className="grid grid-cols-4 gap-6">
                    <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 uppercase mb-1">Business Unit</div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {profile?.legalEntity || profile?.legal_entity || 'Not Specified'}
                        </div>
                    </div>
                    <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 uppercase mb-1">Department</div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {profile?.department || 'Not Specified'}
                        </div>
                    </div>
                    <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 uppercase mb-1">Reporting Manager</div>
                        {reportingManager ? (
                            <div
                                className="flex items-center gap-2 cursor-pointer"
                                onClick={() => handleNavigateToProfile(reportingManager.id)}
                            >
                                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                    {reportingManager.first_name?.[0]}{reportingManager.last_name?.[0]}
                                </div>
                                <div>
                                    <div className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline">
                                        {reportingManager.first_name} {reportingManager.last_name}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="text-sm font-medium text-gray-900 dark:text-white">Not Specified</div>
                        )}
                    </div>
                    <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 uppercase mb-1">Employee ID</div>
                        <button
                            onClick={() => setShowIDCard(true)}
                            className="flex items-center gap-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                        >
                            <CreditCard className="w-4 h-4" />
                            {profile?.employee_code}
                        </button>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
                <div className="border-b dark:border-gray-700">
                    <nav className="flex">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`tour-employee-tab-${tab.id} px-6 py-3 text-sm font-medium border-b-2 ${
                                    activeTab === tab.id
                                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>

                <div className="p-6">
                    {/* About Tab */}
                    {activeTab === 'about' && (
                        <div>
                            {/* Sub-tabs */}
                            <div className="flex gap-4 mb-6 border-b dark:border-gray-700">
                                <button
                                    onClick={() => setAboutSubTab('summary')}
                                    className={`px-4 py-2 text-sm font-medium border-b-2 ${
                                        aboutSubTab === 'summary'
                                            ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                    }`}
                                >
                                    Summary
                                </button>
                                {!permissions.canViewLimitedPanels && (
                                    <>
                                        <button
                                            onClick={() => setAboutSubTab('timeline')}
                                            className={`px-4 py-2 text-sm font-medium border-b-2 ${
                                                aboutSubTab === 'timeline'
                                                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                            }`}
                                        >
                                            Timeline
                                        </button>
                                        <button
                                            onClick={() => setAboutSubTab('wall')}
                                            className={`px-4 py-2 text-sm font-medium border-b-2 ${
                                                aboutSubTab === 'wall'
                                                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                            }`}
                                        >
                                            Wall Activity
                                        </button>
                                    </>
                                )}
                            </div>

                            {/* Summary Sub-tab */}
                            {aboutSubTab === 'summary' && (
                                <div className="grid grid-cols-3 gap-6">
                                    {/* About Section */}
                                    <div className="col-span-2 space-y-6">
                                        <div className="border dark:border-gray-700 rounded-lg p-6">
                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">About</h3>
                                            <div className="space-y-4">
                                                {/* About field */}
                                                <div>
                                                    {editingAbout ? (
                                                        <div className="space-y-2">
                                                            <textarea
                                                                value={aboutText}
                                                                onChange={(e) => setAboutText(e.target.value)}
                                                                placeholder="Tell us about yourself..."
                                                                rows="4"
                                                                disabled={savingAbout}
                                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                            />
                                                            <div className="flex gap-2">
                                                                <button
                                                                    onClick={() => {
                                                                        handleSaveAboutField('about', aboutText);
                                                                        setEditingAbout(false);
                                                                    }}
                                                                    disabled={savingAbout}
                                                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                                                >
                                                                    {savingAbout ? 'Saving...' : 'Save'}
                                                                </button>
                                                                <button
                                                                    onClick={() => {
                                                                        setAboutText(profile?.about || '');
                                                                        setEditingAbout(false);
                                                                    }}
                                                                    disabled={savingAbout}
                                                                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                                                >
                                                                    Cancel
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : aboutText ? (
                                                        <div>
                                                            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{aboutText}</p>
                                                            {isOwnProfile && (
                                                                <button
                                                                    onClick={() => setEditingAbout(true)}
                                                                    className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                                                                >
                                                                    Edit
                                                                </button>
                                                            )}
                                                        </div>
                                                    ) : isOwnProfile ? (
                                                        <button
                                                            onClick={() => setEditingAbout(true)}
                                                            className="text-sm text-blue-600 dark:text-blue-400 border border-blue-600 dark:border-blue-400 px-4 py-2 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20"
                                                        >
                                                            Add your response
                                                        </button>
                                                    ) : (
                                                        <p className="text-sm text-gray-500 dark:text-gray-400 italic">No information provided</p>
                                                    )}
                                                </div>

                                                {/* What I love about my job field */}
                                                <div>
                                                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">What I love about my job?</h4>
                                                    {editingJobLove ? (
                                                        <div className="space-y-2">
                                                            <textarea
                                                                value={jobLoveText}
                                                                onChange={(e) => setJobLoveText(e.target.value)}
                                                                placeholder="What do you love about your job?"
                                                                rows="4"
                                                                disabled={savingAbout}
                                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                            />
                                                            <div className="flex gap-2">
                                                                <button
                                                                    onClick={() => {
                                                                        handleSaveAboutField('what_i_love_about_job', jobLoveText);
                                                                        setEditingJobLove(false);
                                                                    }}
                                                                    disabled={savingAbout}
                                                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                                                >
                                                                    {savingAbout ? 'Saving...' : 'Save'}
                                                                </button>
                                                                <button
                                                                    onClick={() => {
                                                                        setJobLoveText(profile?.what_i_love_about_job || '');
                                                                        setEditingJobLove(false);
                                                                    }}
                                                                    disabled={savingAbout}
                                                                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                                                >
                                                                    Cancel
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : jobLoveText ? (
                                                        <div>
                                                            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{jobLoveText}</p>
                                                            {isOwnProfile && (
                                                                <button
                                                                    onClick={() => setEditingJobLove(true)}
                                                                    className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                                                                >
                                                                    Edit
                                                                </button>
                                                            )}
                                                        </div>
                                                    ) : isOwnProfile ? (
                                                        <button
                                                            onClick={() => setEditingJobLove(true)}
                                                            className="text-sm text-blue-600 dark:text-blue-400 border border-blue-600 dark:border-blue-400 px-4 py-2 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20"
                                                        >
                                                            Add your response
                                                        </button>
                                                    ) : (
                                                        <p className="text-sm text-gray-500 dark:text-gray-400 italic">No information provided</p>
                                                    )}
                                                </div>

                                                {/* My interests and hobbies field */}
                                                <div>
                                                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">My interests and hobbies</h4>
                                                    {editingHobbies ? (
                                                        <div className="space-y-2">
                                                            <textarea
                                                                value={hobbiesText}
                                                                onChange={(e) => setHobbiesText(e.target.value)}
                                                                placeholder="Share your interests and hobbies..."
                                                                rows="4"
                                                                disabled={savingAbout}
                                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                            />
                                                            <div className="flex gap-2">
                                                                <button
                                                                    onClick={() => {
                                                                        handleSaveAboutField('interests_and_hobbies', hobbiesText);
                                                                        setEditingHobbies(false);
                                                                    }}
                                                                    disabled={savingAbout}
                                                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                                                >
                                                                    {savingAbout ? 'Saving...' : 'Save'}
                                                                </button>
                                                                <button
                                                                    onClick={() => {
                                                                        setHobbiesText(profile?.interests_and_hobbies || '');
                                                                        setEditingHobbies(false);
                                                                    }}
                                                                    disabled={savingAbout}
                                                                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                                                >
                                                                    Cancel
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : hobbiesText ? (
                                                        <div>
                                                            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{hobbiesText}</p>
                                                            {isOwnProfile && (
                                                                <button
                                                                    onClick={() => setEditingHobbies(true)}
                                                                    className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                                                                >
                                                                    Edit
                                                                </button>
                                                            )}
                                                        </div>
                                                    ) : isOwnProfile ? (
                                                        <button
                                                            onClick={() => setEditingHobbies(true)}
                                                            className="text-sm text-blue-600 dark:text-blue-400 border border-blue-600 dark:border-blue-400 px-4 py-2 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20"
                                                        >
                                                            Add your response
                                                        </button>
                                                    ) : (
                                                        <p className="text-sm text-gray-500 dark:text-gray-400 italic">No information provided</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Primary Details */}
                                        <div className="border dark:border-gray-700 rounded-lg p-6">
                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Primary Details</h3>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 uppercase mb-1">First Name</div>
                                                    <div className="text-sm text-gray-900 dark:text-white">{profile?.first_name || profile?.firstName}</div>
                                                </div>
                                                <div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 uppercase mb-1">Middle Name</div>
                                                    <div className="text-sm text-gray-900 dark:text-white">{profile?.middle_name || profile?.middleName || 'Not specified'}</div>
                                                </div>
                                                <div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 uppercase mb-1">Last Name</div>
                                                    <div className="text-sm text-gray-900 dark:text-white">{profile?.last_name || profile?.lastName || '.'}</div>
                                                </div>
                                                <div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 uppercase mb-1">Display Name</div>
                                                    <div className="text-sm text-gray-900 dark:text-white">{profile?.display_name || profile?.displayName || 'Not specified'}</div>
                                                </div>
                                                <div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 uppercase mb-1">Gender</div>
                                                    <div className="text-sm text-gray-900 dark:text-white">{profile?.gender || 'Not specified'}</div>
                                                </div>
                                                <div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 uppercase mb-1">Date of Birth</div>
                                                    <div className="text-sm text-gray-900 dark:text-white">
                                                        {formatDateForDisplay(profile?.date_of_birth || profile?.dateOfBirth)}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 uppercase mb-1">Nationality</div>
                                                    <div className="text-sm text-gray-900 dark:text-white">{profile?.nationality || 'Not specified'}</div>
                                                </div>
                                                <div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 uppercase mb-1">Marital Status</div>
                                                    <div className="text-sm text-gray-900 dark:text-white">{profile?.marital_status || 'Not specified'}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Reporting Team Section */}
                                    <div className="col-span-1">
                                        <div className="border dark:border-gray-700 rounded-lg p-6">
                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                                Reporting Team ({reportingTeam.length})
                                            </h3>
                                            <div className="space-y-3">
                                                {reportingTeam.slice(0, 4).map((member) => (
                                                    <div
                                                        key={member.id}
                                                        className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded-lg transition-colors"
                                                        onClick={() => handleNavigateToProfile(member.id)}
                                                    >
                                                        <div
                                                            className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0"
                                                            style={{ backgroundColor: `hsl(${Math.random() * 360}, 70%, 50%)` }}
                                                        >
                                                            {member.first_name?.[0]}{member.last_name?.[0]}
                                                        </div>
                                                        <div className="flex-1">
                                                            <div className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline">
                                                                {member.first_name} {member.last_name}
                                                            </div>
                                                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                                                {member.designation}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                                {reportingTeam.length > 4 && (
                                                    <button className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
                                                        View all (+{reportingTeam.length - 4})
                                                    </button>
                                                )}
                                                {reportingTeam.length === 0 && (
                                                    <p className="text-sm text-gray-500 dark:text-gray-400 italic">No direct reports</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Timeline Sub-tab */}
                            {aboutSubTab === 'timeline' && (
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Timeline</h3>
                                    <div className="relative">
                                        {/* Year Badge */}
                                        <div className="inline-block bg-gray-500 text-white px-3 py-1 rounded text-sm font-medium mb-6">
                                            {getYearFromDate(profile?.joining_date || profile?.joiningDate)}
                                        </div>

                                        {/* Timeline Events */}
                                        <div className="space-y-6 ml-4 border-l-2 border-gray-300 dark:border-gray-600 pl-6">
                                            <div className="relative">
                                                <div className="absolute -left-9 top-0 w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center">
                                                    <UserCircle className="w-4 h-4 text-white" />
                                                </div>
                                                <div>
                                                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                                                        Joined {profile?.legalEntity || profile?.legal_entity || 'Company'}
                                                    </h4>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                                        {formatDateForDisplay(profile?.joining_date || profile?.joiningDate, {
                                                            day: '2-digit',
                                                            month: 'short',
                                                            year: 'numeric'
                                                        })}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Wall Activity Sub-tab */}
                            {aboutSubTab === 'wall' && (
                                <div className="space-y-4">
                                    {/* Section Header */}
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">My Vibe Activity</h3>
                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                Posts you've shared on the Vibe feed
                                            </p>
                                        </div>

                                        {/* Visit Vibe Button - Only show on own profile */}
                                        {isOwnProfile && (
                                            <button
                                                onClick={() => navigate(getVibeRoute())}
                                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                            >
                                                <span>Visit Vibe</span>
                                                <ArrowRight className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>

                                    {/* Vibe Feed with Employee-specific Vibes */}
                                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                                        {console.log('🔍 Wall Activity Debug:', {
                                            isOwnProfile,
                                            profileUserId: profile?.id,
                                            currentUser: currentUser?.id,
                                            filter: isOwnProfile ? "my_vibes" : "user_vibes",
                                            profileName: `${profile?.first_name} ${profile?.last_name}`,
                                            currentUserRole: currentUser?.role
                                        })}
                                        <VibeFeed
                                            currentUser={currentUser}
                                            filter={isOwnProfile ? "my_vibes" : "user_vibes"}
                                            profileUserId={profile?.id}
                                            showHeader={false}
                                            showCreatePost={false}
                                            showSidebar={false}
                                            maxPosts={5}
                                            isProfileView={true}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Profile Tab */}
                    {activeTab === 'profile' && !shouldHideContent('profileTab', permissions) && (
                        <div className="space-y-6">
                            {/* Personal Information */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Personal Information</h3>
                                <div className="grid grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">First Name</label>
                                        <input
                                            type="text"
                                            name="first_name"
                                            value={editMode ? (editedProfile.first_name || editedProfile.firstName || '') : (profile?.first_name || profile?.firstName || '')}
                                            onChange={onEditChange}
                                            disabled={!editMode}
                                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg disabled:bg-gray-50 dark:disabled:bg-gray-700 dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Middle Name</label>
                                        <input
                                            type="text"
                                            name="middle_name"
                                            value={editMode ? (editedProfile.middle_name || editedProfile.middleName || '') : (profile?.middle_name || profile?.middleName || '')}
                                            onChange={onEditChange}
                                            disabled={!editMode}
                                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg disabled:bg-gray-50 dark:disabled:bg-gray-700 dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Last Name</label>
                                        <input
                                            type="text"
                                            name="last_name"
                                            value={editMode ? (editedProfile.last_name || editedProfile.lastName || '') : (profile?.last_name || profile?.lastName || '')}
                                            onChange={onEditChange}
                                            disabled={!editMode}
                                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg disabled:bg-gray-50 dark:disabled:bg-gray-700 dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Display Name</label>
                                        <input
                                            type="text"
                                            name="display_name"
                                            value={editMode ? (editedProfile.display_name || editedProfile.displayName || '') : (profile?.display_name || profile?.displayName || '')}
                                            onChange={onEditChange}
                                            disabled={!editMode}
                                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg disabled:bg-gray-50 dark:disabled:bg-gray-700 dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Personal Email</label>
                                        <input
                                            type="email"
                                            name="personal_email"
                                            value={editMode ? (editedProfile.personal_email || '') : (profile?.personal_email || '')}
                                            onChange={onEditChange}
                                            disabled={!editMode}
                                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg disabled:bg-gray-50 dark:disabled:bg-gray-700 dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={editMode ? (editedProfile.phone || '') : (profile?.phone || '')}
                                            onChange={onEditChange}
                                            disabled={!editMode}
                                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg disabled:bg-gray-50 dark:disabled:bg-gray-700 dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Date of Birth {editMode && <span className="text-red-500">*</span>}
                                        </label>
                                        {editMode ? (
                                            <input
                                                type="date"
                                                name="date_of_birth"
                                                value={editedProfile.date_of_birth || ''}
                                                onChange={onEditChange}
                                                required={true}
                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
                                            />
                                        ) : (
                                            <input
                                                type="text"
                                                value={formatDateForDisplay(profile?.date_of_birth || profile?.dateOfBirth, {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric'
                                                })}
                                                disabled
                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 dark:text-white"
                                            />
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Gender</label>
                                        <select
                                            name="gender"
                                            value={editMode ? (editedProfile.gender || '') : (profile?.gender || '')}
                                            onChange={onEditChange}
                                            disabled={!editMode}
                                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg disabled:bg-gray-50 dark:disabled:bg-gray-700 dark:bg-gray-800 dark:text-white"
                                        >
                                            <option value="">Select</option>
                                            <option value="Male">Male</option>
                                            <option value="Female">Female</option>
                                            <option value="Other">Other</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nationality</label>
                                        <input
                                            type="text"
                                            name="nationality"
                                            value={editMode ? (editedProfile.nationality || '') : (profile?.nationality || '')}
                                            onChange={onEditChange}
                                            disabled={!editMode}
                                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg disabled:bg-gray-50 dark:disabled:bg-gray-700 dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Marital Status</label>
                                        <select
                                            name="marital_status"
                                            value={editMode ? (editedProfile.marital_status || '') : (profile?.marital_status || '')}
                                            onChange={onEditChange}
                                            disabled={!editMode}
                                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg disabled:bg-gray-50 dark:disabled:bg-gray-700 dark:bg-gray-800 dark:text-white"
                                        >
                                            <option value="">Select</option>
                                            <option value="Single">Single</option>
                                            <option value="Married">Married</option>
                                            <option value="Divorced">Divorced</option>
                                            <option value="Widowed">Widowed</option>
                                        </select>
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Address</label>
                                        <textarea
                                            name="address"
                                            value={editMode ? (editedProfile.address || '') : (profile?.address || '')}
                                            onChange={onEditChange}
                                            disabled={!editMode}
                                            rows="3"
                                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg disabled:bg-gray-50 dark:disabled:bg-gray-700 dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">City</label>
                                        <input
                                            type="text"
                                            name="city"
                                            value={editMode ? (editedProfile.city || '') : (profile?.city || '')}
                                            onChange={onEditChange}
                                            disabled={!editMode}
                                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg disabled:bg-gray-50 dark:disabled:bg-gray-700 dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">State</label>
                                        <input
                                            type="text"
                                            name="state"
                                            value={editMode ? (editedProfile.state || '') : (profile?.state || '')}
                                            onChange={onEditChange}
                                            disabled={!editMode}
                                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg disabled:bg-gray-50 dark:disabled:bg-gray-700 dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Country</label>
                                        <input
                                            type="text"
                                            name="country"
                                            value={editMode ? (editedProfile.country || '') : (profile?.country || '')}
                                            onChange={onEditChange}
                                            disabled={!editMode}
                                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg disabled:bg-gray-50 dark:disabled:bg-gray-700 dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Postal Code</label>
                                        <input
                                            type="text"
                                            name="postal_code"
                                            value={editMode ? (editedProfile.postal_code || '') : (profile?.postal_code || '')}
                                            onChange={onEditChange}
                                            disabled={!editMode}
                                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg disabled:bg-gray-50 dark:disabled:bg-gray-700 dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Emergency Contact */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Emergency Contact</h3>
                                <div className="grid grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contact Name</label>
                                        <input
                                            type="text"
                                            name="emergency_contact_name"
                                            value={editMode ? (editedProfile.emergency_contact_name || '') : (profile?.emergency_contact_name || '')}
                                            onChange={onEditChange}
                                            disabled={!editMode}
                                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg disabled:bg-gray-50 dark:disabled:bg-gray-700 dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Relationship</label>
                                        <input
                                            type="text"
                                            name="emergency_contact_relation"
                                            value={editMode ? (editedProfile.emergency_contact_relation || '') : (profile?.emergency_contact_relation || '')}
                                            onChange={onEditChange}
                                            disabled={!editMode}
                                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg disabled:bg-gray-50 dark:disabled:bg-gray-700 dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone Number</label>
                                        <input
                                            type="tel"
                                            name="emergency_contact_phone"
                                            value={editMode ? (editedProfile.emergency_contact_phone || '') : (profile?.emergency_contact_phone || '')}
                                            onChange={onEditChange}
                                            disabled={!editMode}
                                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg disabled:bg-gray-50 dark:disabled:bg-gray-700 dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Profile Tab - Access Denied Message */}
                    {activeTab === 'profile' && shouldHideContent('profileTab', permissions) && (
                        <div className="flex flex-col items-center justify-center py-16">
                            <p className="text-gray-500 dark:text-gray-400">
                                You don't have permission to view this section
                            </p>
                        </div>
                    )}

                    {/* Job Tab */}
                    {activeTab === 'job' && (
                        <div className="space-y-6">
                            {/* Employment Details */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Employment Details</h3>
                                <div className="grid grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Work Email</label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={editMode ? (editedProfile.email || '') : (profile?.email || '')}
                                            onChange={onEditChange}
                                            disabled={!editMode || isOwnProfile}
                                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg disabled:bg-gray-50 dark:disabled:bg-gray-700 dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Employee Code</label>
                                        <input
                                            type="text"
                                            name="employee_code"
                                            value={editMode ? (editedProfile.employee_code || '') : (profile?.employee_code || profile?.employeeNumber || '')}
                                            onChange={onEditChange}
                                            disabled={!editMode || isOwnProfile}
                                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg disabled:bg-gray-50 dark:disabled:bg-gray-700 dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Job Title / Designation</label>
                                        <input
                                            type="text"
                                            name="designation"
                                            value={editMode ? (editedProfile.designation || '') : (profile?.designation || profile?.jobTitle || '')}
                                            onChange={onEditChange}
                                            disabled={!editMode || isOwnProfile}
                                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg disabled:bg-gray-50 dark:disabled:bg-gray-700 dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Legal Entity</label>
                                        {editMode && !isOwnProfile ? (
                                            <select
                                                name="legal_entity"
                                                value={editedProfile.legal_entity || ''}
                                                onChange={onEditChange}
                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
                                            >
                                                <option value="">Select Legal Entity</option>
                                                {legalEntities.map((entity) => (
                                                    <option key={entity.id} value={entity.name}>
                                                        {entity.name}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            <input
                                                type="text"
                                                value={profile?.legalEntity || profile?.legal_entity || 'Not Specified'}
                                                disabled
                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 dark:text-white"
                                            />
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Department</label>
                                        {editMode && !isOwnProfile ? (
                                            <select
                                                name="department_id"
                                                value={editedProfile.department_id || editedProfile.department || ''}
                                                onChange={onEditChange}
                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
                                            >
                                                <option value="">
                                                    {editedProfile.legal_entity
                                                        ? 'Select Department'
                                                        : 'Select Department (All Legal Entities)'}
                                                </option>
                                                {departments.map((dept) => (
                                                    <option key={dept.id || dept.name} value={dept.name}>
                                                        {dept.name}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            <input
                                                type="text"
                                                value={(() => {
                                                    // Try to find department name from departments array
                                                    const dept = departments.find(d => d.id === profile?.department_id || d.name === profile?.department_id || d.name === profile?.department);
                                                    return dept?.name || profile?.department || profile?.department_name || 'Not Specified';
                                                })()}
                                                disabled
                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 dark:text-white"
                                            />
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Employment Type</label>
                                        {editMode && !isOwnProfile ? (
                                            <select
                                                name="employment_type"
                                                value={editedProfile.employment_type || 'full-time'}
                                                onChange={onEditChange}
                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
                                            >
                                                <option value="full-time">Full Time</option>
                                                <option value="part-time">Part Time</option>
                                                <option value="contract">Contract</option>
                                                <option value="intern">Intern</option>
                                            </select>
                                        ) : (
                                            <input
                                                type="text"
                                                value={profile?.timeType || profile?.employment_type || 'Full Time'}
                                                disabled
                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 dark:text-white"
                                            />
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Employment Status</label>
                                        {editMode && !isOwnProfile ? (
                                            <select
                                                name="employment_status"
                                                value={editedProfile.employment_status || 'active'}
                                                onChange={onEditChange}
                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
                                            >
                                                <option value="active">Active</option>
                                                <option value="on-leave">On Leave</option>
                                                <option value="terminated">Terminated</option>
                                                <option value="resigned">Resigned</option>
                                            </select>
                                        ) : (
                                            <input
                                                type="text"
                                                value={profile?.employment_status || 'Active'}
                                                disabled
                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 dark:text-white"
                                            />
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Joining Date</label>
                                        {editMode && !isOwnProfile ? (
                                            <input
                                                type="date"
                                                name="joining_date"
                                                value={editedProfile.joining_date || ''}
                                                onChange={onEditChange}
                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
                                            />
                                        ) : (
                                            <input
                                                type="text"
                                                value={formatDateForDisplay(profile?.joiningDate || profile?.joining_date)}
                                                disabled
                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 dark:text-white"
                                            />
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Work Location</label>
                                        <input
                                            type="text"
                                            name="work_location"
                                            value={editMode ? (editedProfile.work_location || '') : (profile?.work_location || '')}
                                            onChange={onEditChange}
                                            disabled={!editMode || isOwnProfile}
                                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg disabled:bg-gray-50 dark:disabled:bg-gray-700 dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                    <div className="relative">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reporting Manager</label>
                                        {editMode && !isOwnProfile ? (
                                            <>
                                                {/* Display selected manager or search input */}
                                                {editedProfile.managerId ? (
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="text"
                                                            value={employees.find(e => e.id === editedProfile.managerId)?.name || 'Selected Manager'}
                                                            readOnly
                                                            className="flex-1 px-3 py-2 border dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 dark:text-white cursor-not-allowed"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                onEditChange({ target: { name: 'managerId', value: '' } });
                                                                setManagerSearch('');
                                                            }}
                                                            className="px-3 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg border border-red-600 dark:border-red-400 transition-colors"
                                                            title="Clear reporting manager"
                                                        >
                                                            <X className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <input
                                                        type="text"
                                                        value={managerSearch}
                                                        onChange={(e) => {
                                                            setManagerSearch(e.target.value);
                                                            setShowManagerDropdown(true);
                                                        }}
                                                        onFocus={() => setShowManagerDropdown(true)}
                                                        placeholder="Search for manager..."
                                                        className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
                                                    />
                                                )}
                                                {showManagerDropdown && !editedProfile.managerId && (
                                                    <div className="absolute z-50 w-full mt-1 max-h-60 overflow-auto border dark:border-gray-600 rounded-md shadow-lg bg-white dark:bg-gray-800">
                                                        {employees.length === 0 ? (
                                                            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                                                                No employees found
                                                            </div>
                                                        ) : (
                                                            employees
                                                                .filter(emp => {
                                                                    if (emp.id === profile?.id) return false; // Don't show self
                                                                    const searchTerm = managerSearch.toLowerCase();
                                                                    return emp.name?.toLowerCase().includes(searchTerm) ||
                                                                           emp.employee_code?.toLowerCase().includes(searchTerm) ||
                                                                           (emp.designation || '')?.toLowerCase().includes(searchTerm);
                                                                })
                                                                .map((emp) => (
                                                                    <div
                                                                        key={emp.id}
                                                                        onClick={() => {
                                                                            onEditChange({ target: { name: 'managerId', value: emp.id } });
                                                                            setManagerSearch('');
                                                                            setShowManagerDropdown(false);
                                                                        }}
                                                                        className="px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                                                                    >
                                                                        <div className="font-medium text-gray-900 dark:text-white">{emp.name}</div>
                                                                        <div className="text-xs text-gray-500 dark:text-gray-400">
                                                                            {emp.employee_code} • {emp.designation || 'Employee'}
                                                                        </div>
                                                                    </div>
                                                                ))
                                                        )}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <input
                                                type="text"
                                                value={reportingManager ? `${reportingManager.first_name} ${reportingManager.last_name}` : 'N/A'}
                                                disabled
                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 dark:text-white"
                                            />
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Worker Type</label>
                                        {editMode && !isOwnProfile ? (
                                            <select
                                                name="worker_type"
                                                value={editedProfile.worker_type || 'Permanent'}
                                                onChange={onEditChange}
                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
                                            >
                                                <option value="Permanent">Permanent</option>
                                                <option value="Contract">Contract</option>
                                                <option value="Intern">Intern</option>
                                                <option value="Temporary">Temporary</option>
                                            </select>
                                        ) : (
                                            <input
                                                type="text"
                                                value={profile?.worker_type || profile?.workerType || 'Permanent'}
                                                disabled
                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 dark:text-white"
                                            />
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Work Country</label>
                                        <input
                                            type="text"
                                            name="work_country"
                                            value={editMode ? (editedProfile.work_country || editedProfile.country || '') : (profile?.workCountry || profile?.work_country || profile?.country || '')}
                                            onChange={onEditChange}
                                            disabled={!editMode || isOwnProfile}
                                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg disabled:bg-gray-50 dark:disabled:bg-gray-700 dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Probation Period</label>
                                        {editMode && !isOwnProfile ? (
                                            <select
                                                name="probation_period_months"
                                                value={editedProfile.probation_period_months || ''}
                                                onChange={onEditChange}
                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
                                            >
                                                <option value="">Select Duration</option>
                                                <option value="0">No Probation</option>
                                                <option value="1">1 Month</option>
                                                <option value="2">2 Months</option>
                                                <option value="3">3 Months</option>
                                                <option value="6">6 Months</option>
                                                <option value="12">12 Months</option>
                                            </select>
                                        ) : (
                                            <input
                                                type="text"
                                                value={profile?.probation_period_months
                                                    ? `${profile.probation_period_months} Month${profile.probation_period_months !== 1 ? 's' : ''}`
                                                    : profile?.probationPolicy || profile?.probation_policy || 'Not Specified'}
                                                disabled
                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 dark:text-white"
                                            />
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notice Period</label>
                                        <input
                                            type="text"
                                            name="notice_period"
                                            value={editMode ? (editedProfile.notice_period || '') : (profile?.noticePeriod || profile?.notice_period || 'Not Specified')}
                                            onChange={onEditChange}
                                            disabled={!editMode || isOwnProfile}
                                            placeholder={editMode ? "e.g., 30 days, 60 days" : ""}
                                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg disabled:bg-gray-50 dark:disabled:bg-gray-700 dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Work & Attendance Details */}
                            {!shouldHideContent('jobWorkAttendance', permissions) && (
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Work & Attendance Details</h3>
                                <div className="grid grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Leave Plan</label>
                                        {editMode && !isOwnProfile ? (
                                            <select
                                                name="leave_plan"
                                                value={editedProfile.leave_plan || ''}
                                                onChange={onEditChange}
                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
                                            >
                                                <option value="">Select Leave Plan</option>
                                                {leavePlans.map((plan) => (
                                                    <option key={plan.id} value={plan.id}>
                                                        {plan.plan_name || plan.name}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            <input
                                                type="text"
                                                value={(() => {
                                                    const leavePlanId = profile?.leave_plan || profile?.leavePlan;
                                                    const leavePlan = leavePlans.find(p => p.id === leavePlanId);
                                                    return leavePlan?.plan_name || leavePlan?.name || 'Not Assigned';
                                                })()}                                                disabled
                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 dark:text-white"
                                            />
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Holiday List</label>
                                        {editMode && !isOwnProfile ? (
                                            <select
                                                name="holiday_plan_id"
                                                value={editedProfile.holiday_plan_id || editedProfile.holiday_list ||''}
                                                onChange={onEditChange}
                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
                                            >
                                                <option value="">Select Holiday List</option>
                                                {holidayPlans.map((plan) => (
                                                    <option key={plan.id} value={plan.id}>
                                                        {(plan.name || plan.plan_name || plan.title)} ({(plan.country_name || plan.country || "Country")} {plan.year || "Year"})
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            <input
                                                type="text"
                                                value={(() => {
                                                    // Backend stores as holiday_plan_id, check both
                                                    const holidayId = profile?.holiday_plan_id || profile?.holiday_list;
                                                    const holiday = holidayPlans.find(p => p.id === holidayId);
                                                    return holiday ? `${holiday.name || holiday.plan_name} (${holiday.country_name || holiday.country} ${holiday.year})` : 'Not Assigned';
                                                })()}
                                                disabled
                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 dark:text-white"
                                            />
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Shift
                                        </label>
                                        {editMode && !isOwnProfile ? (
                                            <select
                                                name="shift_id"
                                                value={editedProfile.shift_id || editedProfile.shift || ''}
                                                onChange={onEditChange}
                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
                                            >
                                                <option value="">Select Shift</option>
                                                {shifts.map((shift) => (
                                                    <option key={shift.id} value={shift.id}>
                                                        {shift.name} ({shift.start_time} - {shift.end_time})
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            <input
                                                type="text"
                                                value={(() => {
                                                    if (profile?.shift_name) return profile.shift_name;
                                                    const shiftId = profile?.shift_id || profile?.shift;
                                                    const shift = shifts.find(s => s.id == shiftId);
                                                    return shift ? shift.name : 'Not Assigned';
                                                })()}
                                                disabled
                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 dark:text-white"
                                            />
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Weekly Off</label>
                                        {editMode && !isOwnProfile ? (
                                            <select
                                                name="weekly_off"
                                                value={editedProfile.weekly_off || ''}
                                                onChange={onEditChange}
                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
                                            >
                                                <option value="">Select Weekly Off</option>
                                                <option value="Standard Weekly Off Policy">Standard Weekly Off Policy</option>
                                                <option value="Saturday & Sunday">Saturday & Sunday</option>
                                                <option value="Sunday Only">Sunday Only</option>
                                                <option value="Custom">Custom</option>
                                            </select>
                                        ) : (
                                            <input
                                                type="text"
                                                value={profile?.weeklyOff || profile?.weekly_off || 'Standard Weekly Off Policy'}
                                                disabled
                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 dark:text-white"
                                            />
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Attendance Capture Scheme</label>
                                        {editMode && !isOwnProfile ? (
                                            <select
                                                name="attendance_capture_scheme"
                                                value={editedProfile.attendance_capture_scheme || ''}
                                                onChange={onEditChange}
                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
                                            >
                                                <option value="">Select Capture Scheme</option>
                                                <option value="Biometric Capture Scheme">Biometric Capture Scheme</option>
                                                <option value="Manual Entry">Manual Entry</option>
                                                <option value="Mobile App">Mobile App</option>
                                                <option value="Web Portal">Web Portal</option>
                                            </select>
                                        ) : (
                                            <input
                                                type="text"
                                                value={profile?.attendanceCaptureScheme || profile?.attendance_capture_scheme || 'Biometric Capture Scheme'}
                                                disabled
                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 dark:text-white"
                                            />
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Attendance Number</label>
                                        <input
                                            type="text"
                                            name="attendance_number"
                                            value={editMode ? (editedProfile.attendance_number || '') : (profile?.attendanceNumber || profile?.attendance_number || '')}
                                            onChange={onEditChange}
                                            disabled={!editMode || isOwnProfile}
                                            placeholder={editMode ? "Enter attendance ID" : ""}
                                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg disabled:bg-gray-50 dark:disabled:bg-gray-700 dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                </div>
                            </div>
                            )}

                            {/* Compensation Details */}
                            {!shouldHideContent('jobCompensation', permissions) && (
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Compensation Details</h3>
                                <div className="grid grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CTC</label>
                                        <input
                                            type="text"
                                            name="ctc"
                                            value={editMode ? (editedProfile.ctc || '') : (profile?.pay_package?.ctc || profile?.payPackage?.ctc || profile?.ctc || 'Not Specified')}
                                            onChange={onEditChange}
                                            disabled={!editMode || isOwnProfile}
                                            placeholder={editMode ? "Enter CTC amount" : ""}
                                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg disabled:bg-gray-50 dark:disabled:bg-gray-700 dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Currency</label>
                                        {editMode && !isOwnProfile ? (
                                            <select
                                                name="currency"
                                                value={editedProfile.currency || 'INR'}
                                                onChange={onEditChange}
                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
                                            >
                                                <option value="INR">INR</option>
                                                <option value="USD">USD</option>
                                                <option value="EUR">EUR</option>
                                                <option value="GBP">GBP</option>
                                            </select>
                                        ) : (
                                            <input
                                                type="text"
                                                value={profile?.pay_package?.currency || profile?.payPackage?.currency || profile?.currency || 'INR'}
                                                disabled
                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 dark:text-white"
                                            />
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Payroll Frequency</label>
                                        {editMode && !isOwnProfile ? (
                                            <select
                                                name="payroll_frequency"
                                                value={editedProfile.payroll_frequency || 'Monthly'}
                                                onChange={onEditChange}
                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
                                            >
                                                <option value="Monthly">Monthly</option>
                                                <option value="Bi-Weekly">Bi-Weekly</option>
                                                <option value="Weekly">Weekly</option>
                                            </select>
                                        ) : (
                                            <input
                                                type="text"
                                                value={profile?.pay_package?.payroll_frequency || profile?.payPackage?.payroll_frequency || profile?.payrollFrequency || 'Monthly'}
                                                disabled
                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 dark:text-white"
                                            />
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Effective Date</label>
                                        {editMode && !isOwnProfile ? (
                                            <input
                                                type="date"
                                                name="effective_date"
                                                value={editedProfile.effective_date || ''}
                                                onChange={onEditChange}
                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
                                            />
                                        ) : (
                                            <input
                                                type="text"
                                                value={formatDateForDisplay(
                                                    profile?.pay_package?.effective_date ||
                                                    profile?.payPackage?.effective_date ||
                                                    profile?.effectiveDate
                                                ) || 'Not Specified'}
                                                disabled
                                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 dark:text-white"
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                            )}

                            {/* Bank Details */}
                            {!shouldHideContent('jobBankDetails', permissions) && (
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Bank Details</h3>
                                <div className="grid grid-cols-2 gap-6">
                                    <div>
                                        {editMode && !isOwnProfile ? (
                                            <BankDropdown
                                                value={editedProfile.bank_name || ''}
                                                onChange={(value) => onEditChange({ target: { name: 'bank_name', value } })}
                                                theme={currentTheme}
                                                placeholder="Select Bank Name"
                                                className="w-full"
                                            />
                                        ) : (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Bank Name</label>
                                                <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900">
                                                    {profile?.pay_package?.compensation?.bank?.bankName || profile?.bankName || profile?.bank_name || 'Not Specified'}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Account Number</label>
                                        <input
                                            type="text"
                                            name="account_number"
                                            value={editMode ? (editedProfile.account_number || '') : (profile?.pay_package?.compensation?.bank?.accountNumber || profile?.accountNumber || profile?.account_number || 'Not Specified')}
                                            onChange={onEditChange}
                                            disabled={!editMode || isOwnProfile}
                                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg disabled:bg-gray-50 dark:disabled:bg-gray-700 dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">IFSC Code</label>
                                        <input
                                            type="text"
                                            name="ifsc_code"
                                            value={editMode ? (editedProfile.ifsc_code || '') : (profile?.pay_package?.compensation?.bank?.ifscCode || profile?.ifscCode || profile?.ifsc_code || 'Not Specified')}
                                            onChange={onEditChange}
                                            disabled={!editMode || isOwnProfile}
                                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg disabled:bg-gray-50 dark:disabled:bg-gray-700 dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Branch Name</label>
                                        <input
                                            type="text"
                                            name="branch_name"
                                            value={editMode ? (editedProfile.branch_name || '') : (profile?.pay_package?.compensation?.bank?.branchName || profile?.branchName || profile?.branch_name || 'Not Specified')}
                                            onChange={onEditChange}
                                            disabled={!editMode || isOwnProfile}
                                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg disabled:bg-gray-50 dark:disabled:bg-gray-700 dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                </div>
                            </div>
                            )}

                            {/* Tax Information */}
                            {!shouldHideContent('jobTaxInfo', permissions) && (
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Tax Information</h3>
                                <div className="grid grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">PAN Number</label>
                                        <input
                                            type="text"
                                            name="pan_number"
                                            value={editMode ? (editedProfile.pan_number || '') : (profile?.pay_package?.compensation?.tax?.pan || profile?.panNumber || profile?.pan_number || 'Not Specified')}
                                            onChange={onEditChange}
                                            disabled={!editMode || isOwnProfile}
                                            placeholder={editMode ? "ABCDE1234F" : ""}
                                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg disabled:bg-gray-50 dark:disabled:bg-gray-700 dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">UAN Number</label>
                                        <input
                                            type="text"
                                            name="uan_number"
                                            value={editMode ? (editedProfile.uan_number || '') : (profile?.pay_package?.compensation?.tax?.uan || profile?.uanNumber || profile?.uan_number || 'Not Specified')}
                                            onChange={onEditChange}
                                            disabled={!editMode || isOwnProfile}
                                            placeholder={editMode ? "Universal Account Number" : ""}
                                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg disabled:bg-gray-50 dark:disabled:bg-gray-700 dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                </div>
                            </div>
                            )}
                        </div>
                    )}

                    {/* Time Tab */}
                    {activeTab === 'time' && (
                        <TimeTab
                            profile={profile}
                            isOwnProfile={isOwnProfile}
                            permissions={permissions}
                            currentUser={currentUser || user}
                            initialSubTab={searchParams.get('subtab')}
                        />
                    )}

                    {activeTab === 'documents' && (
                        <EmployeeDocuments
                            employeeId={profile?.id}
                            isAdmin={permissions?.canEditDocuments}
                        />
                    )}

                    {/* Activity Logs Tab - Admin/Manager only */}
                    {activeTab === 'logs' && isAdminOrManager && (
                        <ActivityLogsTab
                            employeeId={profile?.id}
                            employeeName={`${profile?.first_name || ''} ${profile?.last_name || ''}`.trim()}
                            initialDate={searchParams.get('date')}
                        />
                    )}
                </div>
            </div>

            {/* Digital ID Card Modal */}
            {showIDCard && (
                <>
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" onClick={() => setShowIDCard(false)} />
                    <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 rounded-xl shadow-2xl z-50 p-6 max-w-md w-full">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Employee ID Card</h3>
                            <button
                                onClick={() => setShowIDCard(false)}
                                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* ID Card Preview */}
                        <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg p-6 text-white mb-6 shadow-lg">
                            <div className="flex items-start gap-4 mb-4">
                                <div className="w-20 h-20 rounded-lg bg-white/20 overflow-hidden flex-shrink-0">
                                    {profile?.profile_picture_url || profile?.photo ? (
                                        <img src={profile.profile_picture_url || profile.photo} alt="Profile" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <User className="w-10 h-10 text-white" />
                                        </div>
                                    )}
                                </div>
                                <div className="flex-1">
                                    <div className="text-xs opacity-75 mb-1">{profile?.legalEntity || profile?.legal_entity || 'Company Name'}</div>
                                    <h4 className="text-lg font-bold">{profile?.first_name} {profile?.last_name}</h4>
                                    <p className="text-sm opacity-90">{profile?.designation}</p>
                                    <p className="text-xs opacity-75 mt-1">{profile?.department}</p>
                                </div>
                            </div>

                            <div className="border-t border-white/20 pt-4 space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span className="opacity-75">Employee ID:</span>
                                    <span className="font-semibold">{profile?.employee_code}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="opacity-75">Department:</span>
                                    <span className="font-semibold">{profile?.department}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="opacity-75">Joined:</span>
                                    <span className="font-semibold">
                                        {formatDateForDisplay(profile?.joining_date || profile?.joiningDate)}
                                    </span>
                                </div>
                            </div>

                            {/* Barcode placeholder */}
                            <div className="mt-4 bg-white p-2 rounded flex items-center justify-center">
                                <div className="flex gap-0.5">
                                    {[...Array(30)].map((_, i) => (
                                        <div
                                            key={i}
                                            className="bg-black"
                                            style={{
                                                width: i % 3 === 0 ? '3px' : '2px',
                                                height: '40px'
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Download Button */}
                        <button
                            onClick={() => {
                                alert('ID Card download functionality will be implemented with PDF generation');
                            }}
                            className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 font-medium"
                        >
                            <Download className="w-5 h-5" />
                            Download ID Card
                        </button>
                    </div>
                </>
            )}

            {/* Award Comp-Off Modal */}
            <AwardCompOffModal
                isOpen={showAwardCompOff}
                onClose={() => setShowAwardCompOff(false)}
                profile={profile}
            />
        </div>
    );
};

export default EmployeeProfileView;
